import {useCallback, useEffect, useMemo, useState} from "react";
import {useAuth} from "@/context/use-auth";
import {Heading} from "@/components/tailwind/heading";
import {Text} from "@/components/tailwind/text";
import {OrderOverviewItem} from "@/models/order";
import {getOrders} from "@/services/order-services";
import {DataTable} from "@/components/data-table";
import {orderColumnsCompleted} from "./order-columns";
import {Loader2} from "lucide-react";
import {useOrdersTranslation} from "@/i18n/hooks";
import {Input, InputGroup} from "@/components/tailwind/input";
import {MagnifyingGlassIcon} from "@heroicons/react/16/solid";
import log from '@/lib/logger'

export default function OrdersCompletedPage() {
    const {token} = useAuth();
    const {t} = useOrdersTranslation();
    const [allOrders, setAllOrders] = useState<OrderOverviewItem[]>([]);
    const [filteredOrders, setFilteredOrders] = useState<OrderOverviewItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        async function loadOrders() {
            setIsLoading(true);
            try {
                const data = await getOrders(token);
                if (data) {
                    const completedOrders = data.filter(order => order.status === 'Completed');
                    setAllOrders(completedOrders);
                }
            } catch (error) {
                log.error(t("orderList.loadingError"), error);
            } finally {
                setIsLoading(false);
            }
        }

        loadOrders();
    }, [token]);

    const filterOrders = useCallback(() => {
        let filtered = [...allOrders];

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(order => {
                const clientName = `${order.client?.firstName || ''} ${order.client?.lastName || ''}`.trim();
                const helperName = `${order.helper?.firstName || ''} ${order.helper?.lastName || ''}`.trim();

                return (
                    order.orderNo?.toLowerCase().includes(searchLower) ||
                    clientName.toLowerCase().includes(searchLower) ||
                    helperName.toLowerCase().includes(searchLower) ||
                    order.service?.toLowerCase().includes(searchLower) ||
                    order.comments?.toLowerCase().includes(searchLower)
                );
            });
        }

        setFilteredOrders(filtered);
    }, [allOrders, searchTerm]);

    useEffect(() => {
        filterOrders();
    }, [filterOrders]);

    const columns = useMemo(() => orderColumnsCompleted(), []);
    const initialSorting = useMemo(() => [{id: "completedAt", desc: true}], []);

    return (
        <div className="container mx-auto py-6">
            <div className="flex justify-between items-center mb-6">
                <Heading>Abgeschlossene Aufträge</Heading>
            </div>

            <div className="w-full relative mb-6 flex justify-end">
                <InputGroup className="max-w-md">
                    <MagnifyingGlassIcon/>
                    <Input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t("orderList.search.placeholder")}
                        aria-label={t("orderList.search.ariaLabel")}
                        data-testid="order-search"
                    />
                </InputGroup>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-primary"/>
                    <span className="ml-2">{t("orderList.loading")}</span>
                </div>
            ) : filteredOrders.length === 0 ? (
                <div className="text-center py-12 bg-secondary/10 rounded-lg">
                    <Text className="text-lg mb-4">
                        {searchTerm
                            ? t("orderList.noMatchingOrders")
                            : "Keine abgeschlossenen Aufträge"}
                    </Text>
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={filteredOrders}
                    resetPageKey={searchTerm}
                    data-testid="orders-table"
                    initialSorting={initialSorting}
                />
            )}
        </div>
    );
}
